import { observable, action } from 'mobx';
import { DateTime } from 'luxon';

import { request, formatApiDate } from 'utils/api';

import BaseStore from './BaseStore';
import { isEqual } from 'lodash';

const VENUE_PATCH_FIELDS = [
  'venueType',
  'categories',
  'attributes',
  'interestTopics',
  'featuredFlags',
  'keywords',
  'name',
  'slug',
  'description',
  'stripeAccount',
  'imageIds',
  'iosImageIds',
  'ticketImageId',
  'webLogoImageId',
  'videoId',
  'location',
  'locationDescription',
  'curbsidePickupInfo',
  'additionalTicketInformation',
  'nearbyVenueIds',
  'floorLevel',
  'parkingDeck',
  'isFeatured',
  'isComingSoon',
  'jibestreamDestinationId',
  'mappedInExternalId',
  'mappedInSpaceId',
  'gatewaySupplierId',
  'snowcloudVenueId',
  'rollerProductCategory',
  'rollerAccessCategory',
  'contentfulVenueId',
  'contentfulLandingPageId',
  'parkScreenId',
  'contactDetails',
  'accessibilityInfo',
  'costIndicator',
  'displayHours',
  'hasTickets',
  'isListed',
  'isPoints',
  'isSeason',
  'isBasketParking',
  'showSessionAvailabilities',
  'availabilitiesDownLimit',
  'hiddenDatePicker',
  'hiddenOperationHours',
  'startFromDate',
  'externalBookings',
  'allowBookingSwap',
  'useCustomSchedules',
  'extBookingsAlertQty',
  'extBookingsAlertEmail',
  'displayOrder',
  'hoursDisplayOrder',
  'startDate',
  'endDate',
  'priceTypes',
  'occupantId',
];

function isPlainObject(value) {
  return Object.prototype.toString.call(value) === '[object Object]';
}

function normalizeReference(value) {
  if (value && isPlainObject(value) && value.id) {
    return value.id;
  }
  return value;
}

function normalizePatchValue(key, value) {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeReference(item));
  }

  if (isPlainObject(value)) {
    return Object.keys(value).reduce((acc, nestedKey) => {
      if (value[nestedKey] === undefined) {
        return acc;
      }

      acc[nestedKey] = normalizePatchValue(nestedKey, value[nestedKey]);
      return acc;
    }, {});
  }

  if (['ticketImageId', 'webLogoImageId', 'videoId'].includes(key)) {
    return normalizeReference(value);
  }

  return value;
}

function pickVenuePatchFields(venue) {
  return VENUE_PATCH_FIELDS.reduce((acc, field) => {
    if (!Object.prototype.hasOwnProperty.call(venue || {}, field)) {
      return acc;
    }

    acc[field] = normalizePatchValue(field, venue[field]);
    return acc;
  }, {});
}

function diffPatchObjects(nextValues, previousValues = {}) {
  return Object.keys(nextValues).reduce((patch, key) => {
    const currentValue = nextValues[key];
    const previousValue = previousValues[key];

    if (isPlainObject(currentValue) && isPlainObject(previousValue)) {
      const nestedPatch = diffPatchObjects(currentValue, previousValue);
      if (Object.keys(nestedPatch).length) {
        patch[key] = nestedPatch;
      }
      return patch;
    }

    if (!isEqual(currentValue, previousValue)) {
      patch[key] = currentValue;
    }

    return patch;
  }, {});
}

function buildVenuePatch(nextVenue, previousVenue = {}) {
  const nextValues = pickVenuePatchFields(nextVenue);
  const previousValues = pickVenuePatchFields(previousVenue);
  return diffPatchObjects(nextValues, previousValues);
}

const BASE_CATEGORIES = ['attraction', 'restaurant', 'shopping'];

export default class VenuesStore extends BaseStore {
  @observable register = new Map();
  @observable items = [];
  @observable totalItems = 0;
  @observable limit = 20;
  @observable page = 1;
  @observable categories = [];
  @observable sort = {
    order: 'asc',
    field: 'name',
  };

  @action
  setSort({ field, order }) {
    this.sort = {
      field,
      order,
    };
  }

  @action
  setPage(page) {
    this.page = page;
  }

  get(id) {
    return this.register.get(id);
  }

  @action
  set(venue) {
    this.register.set(venue.id, venue);
  }

  getByCategory(venueType) {
    return Array.from(this.register)
      .map(([, venue]) => venue)
      .filter((venue) => venue.venueType === venueType);
  }

  parseItem(item) {
    return {
      ...item,
      updatedAt: new Date(item.updatedAt),
      createdAt: new Date(item.createdAt),
    };
  }

  @action
  fetchItem(id, date = new Date(), statusKey = `item:${id}`) {
    const status = this.createStatus(statusKey);
    return request({
      method: 'GET',
      path: `/1/venues/${id}?date=${formatApiDate(date) || ''}`,
    })
      .then(({ data }) => {
        const item = this.parseItem(data);
        this.register.set(item.id, item);
        status.success();
        return item;
      })
      .catch((err) => {
        status.error(err);
        throw err;
      });
  }

  @action
  getAllOpenHours(statusKey = 'getAllOpenHours') {
    const status = this.createStatus(statusKey);
    return request({
      method: 'GET',
      path: '/1/venues/allopenhours',
    })
      .then(({ data }) => {
        status.success();
        return data;
      })
      .catch((err) => {
        status.error(err);
        throw err;
      });
  }

  @action
  fetchItemBySlug(slug, date = new Date(), statusKey = `slug:${slug}`) {
    const status = this.createStatus(statusKey);
    const numDays = 14;
    return request({
      method: 'GET',
      path: `/1/venues/slug/${slug}?date=${
        formatApiDate(date) || ''
      }&numDays=${numDays}`,
    })
      .then(({ data }) => {
        const item = this.parseItem(data);
        this.register.set(item.slug, item);
        status.success();
        return item;
      })
      .catch((err) => {
        status.error(err);
        throw err;
      });
  }

  @action
  fetchVenuesByType(type, statusKey = `type:${type}`) {
    const status = this.createStatus(statusKey);
    return request({
      method: 'GET',
      path: `/1/venues/type/${type}`,
    })
      .then(({ data }) => {
        return data;
      })
      .catch((err) => {
        status.error(err);
        throw err;
      });
  }

  @action
  fetchVenuesByCategory(type, statusKey = `fetchVenuesByCategory`) {
    const status = this.createStatus(statusKey);
    return request({
      method: 'GET',
      path: `/1/venues/category/${type}`,
    })
      .then(({ data }) => {
        const items = data.map((item) => ({
          ...this.parseItem(item),
          id: item._id,
        }));

        return items;
      })
      .catch((err) => {
        status.error(err);
        throw err;
      });
  }

  @action
  fetchItems(
    {
      limit = this.limit,
      date = DateTime.local().toISODate(),
      skip = (this.page - 1) * this.limit,
      isListed = false,
      displayHours = false,
      hasTickets,
      allowBookingSwap,
      name,
      venueType,
      featuredFlags,
      jibestreamDestinationId,
      contentfulVenueId,
      categorySelected = 'all',
      subCategoryName = '',
    } = {},
    statusKey = 'list'
  ) {
    this.register.clear();
    const status = this.createStatus(statusKey);
    return request({
      method: 'POST',
      path: '/1/venues/search',
      body: {
        limit,
        skip,
        date,
        name,
        venueType,
        isListed,
        displayHours,
        hasTickets,
        allowBookingSwap,
        featuredFlags,
        jibestreamDestinationId,
        contentfulVenueId,
        sort: this.sort,
        subCategoryName,
      },
    })
      .then(({ data, meta }) => {
        const settedFeaturedSubCategories = ['now-open', 'coming-soon'];
        if (
          categorySelected.indexOf('all') === -1 &&
          !settedFeaturedSubCategories.includes(categorySelected)
        ) {
          const currentCategory = this.categories.find(
            (category) => category.route === categorySelected
          );
          data = data.filter((item) =>
            item.categories.includes(currentCategory.name)
          );
        }
        if (settedFeaturedSubCategories.includes(categorySelected)) {
          const currentCategory = this.categories.find(
            (category) => category.route === categorySelected
          );
          data = data.filter((item) =>
            item.featuredFlags.includes(currentCategory.name)
          );
        }
        const items = data.map((item) => this.parseItem(item));
        items.forEach((item) => {
          this.register.set(item.id, item);
        });
        this.totalItems = meta.total;
        this.items.replace(items);
        status.success();
        return [items, meta.total];
      })
      .catch((err) => {
        status.error(err);
        throw err;
      });
  }

  @action
  fetchDirectoryItems(
    {
      limit = this.limit,
      skip = (this.page - 1) * this.limit,
      name,
      venueType,
      subCategoryName = '',
    } = {},
    statusKey = 'directory-list'
  ) {
    this.register.clear();
    const status = this.createStatus(statusKey);
    return request({
      method: 'POST',
      path: '/1/venues/directory/search',
      body: {
        limit,
        skip,
        name,
        venueType,
        subCategoryName,
        sort: this.sort,
      },
    })
      .then(({ data, meta }) => {
        const items = data.map((item) => this.parseItem(item));
        items.forEach((item) => {
          this.register.set(item.id, item);
        });
        this.totalItems = meta.total;
        this.items.replace(items);
        status.success();
        return [items, meta.total];
      })
      .catch((err) => {
        status.error(err);
        throw err;
      });
  }

  @action
  fetchCategories(_, statusKey = 'categories') {
    if (this.categories.length) {
      return Promise.resolve(this.categories);
    }

    const status = this.createStatus(statusKey);
    return request({
      method: 'GET',
      path: '/1/metadata/category',
    })
      .then(({ data }) => {
        const createRoute = (str) =>
          str
            .replaceAll(/[^0-9a-z\s+]/gi, '')
            .replace(/ +/g, '-')
            .trim()
            .toLowerCase();
        const defaultCategories = [
          {
            categoryType: 'attraction',
            name: 'All Entertainment',
            route: createRoute('All Entertainments'),
            order: 0,
          },
          {
            categoryType: 'shopping',
            name: 'All Stores',
            route: createRoute('All Stores'),
            order: 0,
          },
          {
            categoryType: 'restaurant',
            name: 'All Food & Drink',
            route: createRoute('All Food & Drink'),
            order: 0,
          },
        ];
        const finalCategories = data
          .filter(
            (category) => !BASE_CATEGORIES.includes(category.name.toLowerCase())
          )
          .map((category) => {
            return {
              categoryType: category.type,
              name: category.name,
              order: category.order,
              route: createRoute(category.name),
            };
          });
        this.categories.replace([...defaultCategories, ...finalCategories]);
        status.success();
        return [...defaultCategories, ...finalCategories];
      })
      .catch((err) => {
        status.error(err);
        throw err;
      });
  }

  @action
  create(body, statusKey = 'create') {
    const status = this.createStatus(statusKey);
    return request({
      method: 'POST',
      path: '/1/venues',
      body,
    })
      .then(({ data }) => {
        const item = this.parseItem(data);
        this.register.set(item.id, item);

        return item;
      })
      .catch((err) => {
        status.error(err);
        throw err;
      });
  }

  @action
  update(body, originalBodyOrStatusKey = null, statusKey = 'update') {
    let originalBody = originalBodyOrStatusKey;
    if (typeof originalBodyOrStatusKey === 'string') {
      statusKey = originalBodyOrStatusKey;
      originalBody = null;
    }

    const status = this.createStatus(statusKey);
    const { id, ...rest } = body;
    const patchBody = originalBody
      ? buildVenuePatch(rest, originalBody)
      : pickVenuePatchFields(rest);

    if (!Object.keys(patchBody).length) {
      const item = this.register.get(id) || this.parseItem(body);
      this.register.set(item.id, item);
      status.success();
      return Promise.resolve(item);
    }

    return request({
      method: 'PATCH',
      path: `/1/venues/${id}`,
      body: patchBody,
    })
      .then(({ data }) => {
        const item = this.parseItem(data);
        this.register.set(item.id, item);
        return item;
      })
      .catch((err) => {
        status.error(err);
        throw err;
      });
  }

  @action
  delete(item, statusKey = 'delete') {
    const status = this.createStatus(statusKey);
    return request({
      method: 'DELETE',
      path: `/1/venues/${item.id}`,
    })
      .then(() => {
        this.register.delete(item.id);
        return this.fetchItems({}, false).then(() => {
          status.success();
          return item;
        });
      })
      .catch((err) => {
        status.error(err);
        throw err;
      });
  }

  @action
  fetchExternalBookingsVenues(statusKey = `externalBookingsVenues`) {
    const status = this.createStatus(statusKey);
    return request({
      method: 'GET',
      path: `/1/venues/externalbookings`,
    })
      .then(({ data }) => {
        return data;
      })
      .catch((err) => {
        status.error(err);
        throw err;
      });
  }

  @action
  generateKeywords(venueId, prompt) {
    return request({
      method: 'POST',
      path: `/1/venues/generate-keywords/${venueId}`,
      body: {
        prompt,
      },
    })
      .then((data) => {
        return data;
      })
      .catch((err) => {
        throw err;
      });
  }

  @action
  fetchPriceTypes(venueId, statusKey = 'fetchPriceTypes') {
    const status = this.createStatus(statusKey);
    return request({
      method: 'GET',
      path: `/1/control-tower/venue-prices/${venueId}`,
    })
      .then(({ data }) => data)
      .catch((err) => {
        status.error(err);
        throw err;
      });
  }
}
